import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import type { ConversationEmailPurpose, FollowUpFormValues, FollowUpMessage, UseConversationFollowUpOptions } from "@/types/voice/conversation-follow-up";

/** Match explicit requests for help, not silence or elapsed time. */
const needsFollowUp = (messages: FollowUpMessage[]) => messages.slice(-4).some(({ message, isUser }) =>
  isUser
    ? /\b(speak|talk) to (a |the )?(human|person|someone|support|team)\b|\b(email me|contact support|still (not working|having trouble)|didn['’]?t (help|work)|doesn['’]?t (help|work))\b/i.test(message)
    : /\b(i (can['’]?t|cannot|couldn['’]?t|am unable to)|contact (our |the )?(support|team)|human (help|support))\b/i.test(message),
);

/** The existing email-question endpoint accepts at most 4,000 characters. */
export const buildConversationFollowUp = (messages: FollowUpMessage[], conversationId?: string | null, purpose: ConversationEmailPurpose = "follow_up") => {
  const request = purpose === "summary"
    ? "The visitor requests an email summary of the conversation below. Reply to the visitor with a concise recap of the topics discussed, useful answers, recommendations and agreed next steps. Use only the supplied conversation; do not invent details. If earlier context is omitted, say this is a recap of the recent messages."
    : "Please follow up with this visitor by email about their conversation.";
  const header = `${request}${conversationId ? `\nConversation reference: ${conversationId}` : ""}\n\n`;
  const transcript = messages.filter((turn) => turn.message.trim())
    .map((turn) => `${turn.isUser ? "Visitor" : "Genie"}: ${turn.message.trim()}`).join("\n\n");
  const truncationNotice = "[Earlier conversation omitted; recent context follows.]\n";
  const available = 4000 - header.length;
  return header + (transcript.length <= available ? transcript : truncationNotice + transcript.slice(-(available - truncationNotice.length)));
};

export const useConversationFollowUp = ({
  agentId, initialEmail, conversationId, sessionKey, messages, enabled, pageUrl, askQuestion, onCaptured,
}: UseConversationFollowUpOptions) => {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [requestContext, setRequestContext] = useState("");
  const [purpose, setPurpose] = useState<ConversationEmailPurpose>("follow_up");
  const form = useForm<FollowUpFormValues>({ defaultValues: { email: initialEmail ?? "" } });
  const submittingRef = useRef(false);
  const identityRef = useRef({ agentId, sessionKey });
  const mutation = useMutation({
    mutationFn: async (values: FollowUpFormValues) => {
      if (!enabled || !agentId || !requestContext) throw new Error("Start a conversation before requesting email follow-up.");
      const identity = identityRef.current;
      const email = values.email.trim();
      const result = await askQuestion({ visitorEmail: email, question: requestContext, pageUrl });
      if (!result.ok) {
        throw new Error(result.errorCode === "rate_limited"
          ? "You've sent a few requests recently. Please try again in a little while."
          : result.errorCode === "no_mailbox"
            ? "Email follow-up isn't available for this Genie right now. Please use the support page or keep chatting."
            : "Your request wasn't sent. Please try again or keep chatting.");
      }
      return { email, identity };
    },
    onSuccess: ({ email, identity }) => {
      if (identity !== identityRef.current) return;
      setDismissed(true);
      onCaptured?.(email);
    },
  });
  const { reset } = mutation;
  useEffect(() => {
    // A transport may clear its ID before the end callback supplies lastConversationId.
    // That gap must not discard a visitor's pending email request.
    if (identityRef.current.agentId === agentId && (!sessionKey || identityRef.current.sessionKey === sessionKey)) return;
    identityRef.current = { agentId, sessionKey };
    setOpen(false);
    setDismissed(false);
    setRequestContext("");
    setPurpose("follow_up");
    reset();
  }, [agentId, sessionKey, reset]);

  const hasConversation = messages.some((turn) => turn.isUser && turn.message.trim());
  const available = enabled && !!agentId && hasConversation;
  const openRequest = (nextPurpose: ConversationEmailPurpose) => {
    if (!available) return;
    // Do not change the recipient's agreed request while it is in flight.
    if (submittingRef.current || mutation.isPending) return;
    if (!form.getValues("email") && initialEmail) form.setValue("email", initialEmail);
    // Reopening a failed request preserves its frozen context for retry.
    // Switching purpose must clear the previous purpose's success/error state.
    if (nextPurpose !== purpose || !requestContext || (!mutation.isError && !mutation.isSuccess)) {
      reset();
      setPurpose(nextPurpose);
      setRequestContext(buildConversationFollowUp(messages, conversationId, nextPurpose));
    }
    // Freeze what the visitor agrees to share while the conversation carries on.
    setOpen(true);
  };
  const submit = form.handleSubmit(async (values) => {
    if (submittingRef.current || mutation.isSuccess) return;
    submittingRef.current = true;
    try {
      await mutation.mutateAsync(values);
    } catch {
      // Keep the sheet and email intact; mutation.error supplies retry feedback.
    } finally {
      submittingRef.current = false;
    }
  });

  return {
    open, openSheet: () => openRequest("follow_up"), openSummarySheet: () => openRequest("summary"),
    purpose, contextTruncated: requestContext.includes("[Earlier conversation omitted; recent context follows.]"),
    close: () => setOpen(false), form, submit,
    available, dismissed, showPrompt: available && !dismissed && needsFollowUp(messages),
    dismiss: () => setDismissed(true),
    isPending: mutation.isPending, isSuccess: mutation.isSuccess,
    error: mutation.error?.message ?? null,
  };
};
